import type { MaterializedMenuProduct } from '@iheartjane/data-access';
import type { SmartSort } from '@iheartjane/dm-sdk';
import { IntendedDisplay } from '@iheartjane/dm-sdk';
import { FLAGS } from '@iheartjane/feature-flags';
import { Flex } from '@iheartjane/reefer';
import { SEARCH_POSITION } from '@iheartjane/types';

import { CommunicationBanner } from '@jane/bloom/components/communicationBanner';
import { BloomCarousel } from '@jane/bloom/components/communicationBanner/carouselBanner/bloomCarousel';
import { CategorySelector } from '@jane/bloom/components/productListingPage/categoryAndFilterSelectors/categorySelector';
import { SearchContentHeader } from '@jane/bloom/components/search/searchContentHeader';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { useTypedRouteLoaderData } from '@jane/bloom/hooks/useTypedRouteLoaderData';
import type { CarouselBanner } from '@jane/bloom/types/schemas/roots/carouselBanner';
import type { CommunicationBannerViewModel } from '@jane/bloom/viewModels/communicationBanner';

import styles from './menuHeader.module.css';

export type MenuHeaderBanners = {
  communicationBanner: CommunicationBannerViewModel;
  desktopCarouselBanner: CarouselBanner;
  mobileCarouselBanner: CarouselBanner;
  showCarousel: boolean;
  showImageBanner: boolean;
  storeName?: string;
};

type MenuHeaderProps = {
  // Only the featured page has CMS banner data; PLP pages render tabs only.
  banners?: MenuHeaderBanners;
  customImageAdRow?: SmartSort<MaterializedMenuProduct>;
};

// Keeps tabs + carousel + comms banner in one place so upcoming menu-theme
// params can reorder them (carousel above tabs; prominent search above carousel).
export const MenuHeader = ({ banners, customImageAdRow }: MenuHeaderProps) => {
  const { featureFlags, theme } = useRootLoaderData();
  const menuLayoutData = useTypedRouteLoaderData('routes/_menu');

  const useCustomComponents = featureFlags[FLAGS.c2CustomComponents];
  const categoriesAboveCarousel = useCustomComponents
    ? theme?.bloom?.category_tabs?.position === 'above_carousel'
    : true;
  const showContentHeaderSearch =
    useCustomComponents &&
    theme?.bloom?.search?.position === SEARCH_POSITION.content_header;
  const displayCategories = menuLayoutData?.displayCategories;

  const desktopCarouselItems = banners?.showCarousel
    ? banners.desktopCarouselBanner
    : [];
  const mobileCarouselItems = banners?.showCarousel
    ? banners.mobileCarouselBanner
    : [];
  const showDesktopCarousel =
    desktopCarouselItems.length > 0 || !!customImageAdRow;
  const showMobileCarousel =
    mobileCarouselItems.length > 0 || !!customImageAdRow;

  return (
    <Flex
      className={styles['container']}
      flexDirection="column"
      width="100%"
      gap={24}
    >
      {displayCategories && categoriesAboveCarousel && (
        <CategorySelector displayCategories={displayCategories} />
      )}
      {showContentHeaderSearch && <SearchContentHeader />}
      {banners && !categoriesAboveCarousel && (
        <CommunicationBanner
          communicationBanner={banners.communicationBanner}
          showImageBanner={banners.showImageBanner}
        />
      )}
      {showDesktopCarousel && (
        <div className="desktop-carousel-banner">
          <BloomCarousel
            adIntendedDisplay={IntendedDisplay.Desktop}
            customImageAdRow={customImageAdRow}
            items={desktopCarouselItems}
            storeName={banners?.storeName}
          />
        </div>
      )}
      {showMobileCarousel && (
        <div className="mobile-carousel-banner">
          <BloomCarousel
            adIntendedDisplay={IntendedDisplay.Mobile}
            customImageAdRow={customImageAdRow}
            items={mobileCarouselItems}
            storeName={banners?.storeName}
          />
        </div>
      )}
      {banners && categoriesAboveCarousel && (
        <CommunicationBanner
          communicationBanner={banners.communicationBanner}
          showImageBanner={banners.showImageBanner}
        />
      )}
      {displayCategories && !categoriesAboveCarousel && (
        <CategorySelector displayCategories={displayCategories} />
      )}
    </Flex>
  );
};
